import { create, type StateCreator } from 'zustand';
import { createStore } from 'zustand/vanilla';

import {
	DEFAULT_WORKSPACE_MODE_PREFERENCE,
	normalizeWorkspaceModePreference,
	type WorkspaceGraphMode,
	type WorkspaceMode,
	type WorkspaceModePreference,
} from '../../../core/ports/preferences-repository';

export interface WorkspaceModeState {
	isHydrated: boolean;
	currentMode: WorkspaceMode;
	currentGraphMode: WorkspaceGraphMode;
	hydrate(preference?: Partial<WorkspaceModePreference> | null): void;
	setMode(mode: WorkspaceMode): void;
	returnToGraphMode(): void;
	asPreference(): WorkspaceModePreference;
}

function createWorkspaceModeState(initialPreference?: Partial<WorkspaceModePreference> | null): StateCreator<WorkspaceModeState> {
	return (set, get) => {
		const normalized = normalizeWorkspaceModePreference(initialPreference);

		return {
			isHydrated: false,
			currentMode: normalized.workspaceMode,
			currentGraphMode: normalized.lastGraphMode,
			hydrate(preference) {
				const next = normalizeWorkspaceModePreference(preference);
				set({
					isHydrated: true,
					currentMode: next.workspaceMode,
					currentGraphMode: next.lastGraphMode,
				});
			},
			setMode(mode) {
				if (mode === 'outline') {
					set({ currentMode: 'outline' });
					return;
				}

				set({
					currentMode: mode,
					currentGraphMode: mode,
				});
			},
			returnToGraphMode() {
				set((state) => ({ currentMode: state.currentGraphMode }));
			},
			asPreference() {
				return selectWorkspaceModePreference(get());
			},
		};
	};
}

export function createWorkspaceModeStore(initialPreference?: Partial<WorkspaceModePreference> | null) {
	return createStore<WorkspaceModeState>(createWorkspaceModeState(initialPreference));
}

export function selectWorkspaceModePreference(state: Pick<WorkspaceModeState, 'currentMode' | 'currentGraphMode'>): WorkspaceModePreference {
	return normalizeWorkspaceModePreference({
		workspaceMode: state.currentMode,
		lastGraphMode: state.currentGraphMode,
	});
}

export const useWorkspaceModeStore = create<WorkspaceModeState>(createWorkspaceModeState(DEFAULT_WORKSPACE_MODE_PREFERENCE));